import React, { useEffect, useState, useCallback } from "react";
import api from "../../lib/api";
import { formatDateTime } from "../../lib/format";
import { toast } from "sonner";
import { Wind, ShieldWarning, ShieldCheck } from "@phosphor-icons/react";

export default function WeatherTab() {
  const [w, setW] = useState(null);
  const [protocol, setProtocol] = useState(null);
  const [checklist, setChecklist] = useState([]);
  const [confirm, setConfirm] = useState(null); // 'activate' | 'deactivate' | null

  const load = useCallback(() => {
    api.get("/weather").then(r => setW(r.data));
    api.get("/weather/protocol").then(r => setProtocol(r.data));
    api.get("/weather/checklist").then(r => setChecklist(r.data.items));
  }, []);
  useEffect(() => { load(); }, [load]);

  const toggleItem = async (id, done) => {
    await api.post("/weather/checklist/toggle", { item_id: id, done });
    load();
  };

  const activate = async () => {
    try { await api.post("/weather/protocol/activate", { confirm: true }); toast.success("Protocolo activado"); }
    catch (e) { toast.error(e.response?.data?.detail || "Error"); }
    setConfirm(null); load();
    window.dispatchEvent(new Event("quilla:protocol-changed"));
  };
  const deactivate = async () => {
    try { await api.post("/weather/protocol/deactivate", { confirm: true }); toast.success("Protocolo desactivado"); }
    catch (e) { toast.error(e.response?.data?.detail || "Error"); }
    setConfirm(null); load();
    window.dispatchEvent(new Event("quilla:protocol-changed"));
  };

  if (!w) return <div className="q-card p-6">Cargando clima…</div>;
  if (!w.available) return <div className="q-card p-6 text-[color:var(--q-muted)]">Coordenadas del hotel no configuradas.</div>;

  const alertColor = w.alert === "huracan" ? "#ef4444" : w.alert === "tormenta_tropical" ? "#f59e0b" : w.alert === "vientos_fuertes" ? "#C9A227" : "#10b981";
  const active = protocol?.active === true;

  return (
    <div className="max-w-4xl grid gap-4">
      <div className="q-card p-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <div className="q-label text-[color:var(--q-muted)]">Clima · Open-Meteo (datos reales)</div>
            <h2 className="text-2xl">{w.current?.temperature_2m ? w.current.temperature_2m.toFixed(1) + "°C" : "—"}</h2>
          </div>
          <div className="text-right">
            <div className="q-label mb-1" style={{color: alertColor}}>Nivel: {w.alert.replace("_", " ")}</div>
            <div className="text-xs text-[color:var(--q-muted)] font-mono">
              viento máx {Math.round(w.max_wind_kmh || 0)} · ráfagas {Math.round(w.max_gust_kmh || 0)} km/h · lluvia {Math.round(w.max_precip_mm || 0)} mm
            </div>
            {w.reasons?.length > 0 && (
              <div className="text-xs mt-1">Motivos: {w.reasons.join(" · ")}</div>
            )}
          </div>
        </div>
        {w.hurricane_season && (
          <div className="text-xs bg-amber-50 border border-amber-200 rounded p-3 text-amber-800">
            Temporada de huracanes (jun–nov). Mantén el protocolo listo.
          </div>
        )}
      </div>

      <div className="q-card p-6">
        <div className="flex items-center justify-between mb-3">
          <div>
            <h3 className="font-semibold text-lg">Protocolo de huracán</h3>
            <div className="text-xs text-[color:var(--q-muted)]">Activación manual · queda en bitácora de auditoría</div>
          </div>
          {active ? (
            <button onClick={() => setConfirm("deactivate")} data-testid="deactivate-protocol"
              className="q-btn-ghost flex items-center gap-2 border-emerald-600 text-emerald-700">
              <ShieldCheck size={14} weight="fill"/> Desactivar protocolo
            </button>
          ) : (
            <button onClick={() => setConfirm("activate")} data-testid="activate-protocol"
              className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg font-semibold flex items-center gap-2">
              <ShieldWarning size={14} weight="fill"/> Activar protocolo
            </button>
          )}
        </div>

        {active && (
          <div className="p-3 rounded-lg bg-red-50 border border-red-200 mb-4 text-sm">
            <div className="font-semibold text-red-700">Protocolo ACTIVO</div>
            <div className="text-xs text-red-800">Activado por {protocol.activated_by} · {formatDateTime(protocol.activated_at)}</div>
          </div>
        )}
        {!active && protocol?.deactivated_at && (
          <div className="text-xs text-[color:var(--q-muted)] mb-3">Última desactivación: {formatDateTime(protocol.deactivated_at)} por {protocol.deactivated_by}.</div>
        )}

        <div className="space-y-2">
          {checklist.map(it => (
            <label key={it.id} className="flex items-center gap-3 p-2 rounded cursor-pointer hover:bg-neutral-50" data-testid={`checklist-item-${it.id}`}>
              <input type="checkbox" checked={it.done} onChange={(e) => toggleItem(it.id, e.target.checked)} disabled={!active} className="h-4 w-4"/>
              <span className={`text-sm ${it.done ? "line-through text-[color:var(--q-muted)]" : ""} ${!active ? "text-[color:var(--q-muted)]" : ""}`}>{it.text}</span>
            </label>
          ))}
        </div>
      </div>

      {confirm && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={() => setConfirm(null)}>
          <div className="q-card w-full max-w-md p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center gap-3 mb-3">
              {confirm === "activate" ? (
                <ShieldWarning size={28} className="text-red-600" weight="fill"/>
              ) : (
                <ShieldCheck size={28} className="text-emerald-600" weight="fill"/>
              )}
              <h3 className="text-lg font-semibold">
                {confirm === "activate" ? "¿Activar protocolo de huracán?" : "¿Desactivar protocolo?"}
              </h3>
            </div>
            <p className="text-sm text-[color:var(--q-muted)] mb-4">
              {confirm === "activate"
                ? "Se enviará una notificación crítica con sirena a TODO el personal del hotel. Queda registrado en bitácora."
                : "Se notifica a todo el personal el fin del protocolo y se levanta el banner de alerta."}
            </p>
            <div className="flex gap-2 justify-end">
              <button onClick={() => setConfirm(null)} className="q-btn-ghost">Cancelar</button>
              {confirm === "activate" ? (
                <button data-testid="confirm-activate" onClick={activate} className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg font-semibold">
                  Sí, activar
                </button>
              ) : (
                <button data-testid="confirm-deactivate" onClick={deactivate} className="q-btn-primary">Sí, desactivar</button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
